import axios from "axios";
import { apiClient } from "./client";

/** Effective display state (design §0.1): derived + stored. */
export type DisplayStatus =
  | "WAITING"
  | "CALLED"
  | "SERVING"
  | "HELD"
  | "SKIPPED"
  | "DONE";

export interface QueueEntry {
  queueNumber: number;
  patientCode: string;
  patientNameMasked: string;
  doctorName: string;
  serviceName: string;
  startAt: string;
  status: DisplayStatus;
  registrationNo: number | null;
  roomId: string | null;
  roomName: string | null;
  roomNo: number | null;
  /** Appointment id — for staff/room quick actions. */
  appointmentId: string | null;
  priority: boolean;
}

export interface QueueCounts {
  waiting: number;
  called: number;
  serving: number;
  done: number;
}

export interface QueueDisplay {
  date: string;
  /** Tên phòng (từ cấu hình phòng, theo `room` id) — có cả khi hàng đợi rỗng. Null ở chế độ toàn chi nhánh. */
  roomName: string | null;
  /** Số "Mời chuẩn bị" từ cấu hình phòng (layout C mặc định; URL ?prep= ghi đè). */
  prepareGroupSize: number;
  counts: QueueCounts;
  entries: QueueEntry[];
}

/** Shared multi-room board (View D) — per-room {đang khám · đang mời · kế tiếp}, STT only. */
export interface RoomQueue {
  roomId: string;
  roomName: string | null;
  serving: number | null;
  called: number | null;
  next: number[];
}

export interface MultiRoomDisplay {
  date: string;
  rooms: RoomQueue[];
}

/**
 * Staff path — uses the shared JWT-bearing axios instance. Operators open
 * /display/queue?branchId=<uuid> from a logged-in machine.
 */
export const queueDisplayApi = {
  async fetch(
    branchId: string,
    date?: string | null,
    room?: string | null
  ): Promise<QueueDisplay> {
    const res = await apiClient.get<QueueDisplay>("/v1/queue/display", {
      params: { branchId, date: date ?? undefined, room: room ?? undefined },
    });
    return res.data;
  },
  async fetchMulti(
    branchId: string,
    date?: string | null
  ): Promise<MultiRoomDisplay> {
    const res = await apiClient.get<MultiRoomDisplay>("/v1/queue/display/multi", {
      params: { branchId, date: date ?? undefined },
    });
    return res.data;
  },
};

/**
 * Kiosk path — unauthenticated, opaque token in the URL. The TV is
 * configured once with /display/queue?token=<token>. We do NOT use the
 * shared apiClient here so the Authorization / X-Addy-Tenant-Id headers
 * are never attached to the request — the public endpoint refuses them
 * and the kiosk machine often has neither.
 */
const PUBLIC_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export const publicQueueApi = {
  async fetch(
    token: string,
    date?: string | null,
    room?: string | null
  ): Promise<QueueDisplay> {
    const res = await axios.get<QueueDisplay>(
      `${PUBLIC_BASE}/v1/public/queue/display`,
      {
        params: { token, date: date ?? undefined, room: room ?? undefined },
        timeout: 30_000,
      }
    );
    return res.data;
  },
  async fetchMulti(
    token: string,
    date?: string | null
  ): Promise<MultiRoomDisplay> {
    const res = await axios.get<MultiRoomDisplay>(
      `${PUBLIC_BASE}/v1/public/queue/display/multi`,
      { params: { token, date: date ?? undefined }, timeout: 30_000 }
    );
    return res.data;
  },
};
